import React, { memo } from 'react';
import styled from 'styled-components';
import ModalContainer, { ButtonGroup } from '@/shared/components/modal/ModalContainer';
import StyledButton from '@/shared/components/atoms/StyledButton';
import theme from '@/styles/theme';
import type { OnsiteFailedRowDto } from '@/api/generated/model';

type OnsiteUploadResultModalProps = {
	successCount: number;
	failedRows: OnsiteFailedRowDto[];
	onDownloadFailedRows: () => void;
	onClose: () => void;
};

// 현장 접수 수동 업로드 결과 모달 (성공/실패 건수 + 행별 실패 사유)
const OnsiteUploadResultModal = memo<OnsiteUploadResultModalProps>(({ successCount, failedRows, onDownloadFailedRows, onClose }) => (
	<ModalContainer maxWidth={600} title="업로드 결과" onClose={onClose}>
		<Summary>
			<SuccessCount>성공 {successCount}건</SuccessCount>
			{failedRows.length > 0 && <FailedCount>실패 {failedRows.length}건</FailedCount>}
		</Summary>

		{failedRows.length > 0 && (
			<FailedList>
				{failedRows.map(row => (
					<FailedRow key={row.rowIndex}>
						<RowIndex>{row.rowIndex}행</RowIndex>
						<RowEmail>{row.email}</RowEmail>
						<RowReason>{row.reason}</RowReason>
					</FailedRow>
				))}
			</FailedList>
		)}

		<ButtonGroup>
			{failedRows.length > 0 && (
				<StyledButton width={160} fontColor={theme.colors.deepGrayFontColor} onClick={onDownloadFailedRows}>
					실패 목록 다운로드
				</StyledButton>
			)}
			<StyledButton width={160} fontColor={theme.colors.whiteColor} bgColor={theme.colors.blackColor} border="none" onClick={onClose}>
				확인
			</StyledButton>
		</ButtonGroup>
	</ModalContainer>
));

OnsiteUploadResultModal.displayName = 'OnsiteUploadResultModal';

export default OnsiteUploadResultModal;

const Summary = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;
const SuccessCount = styled.span`
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
	color: ${theme.colors.activeBlue};
`;
const FailedCount = styled.span`
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
	color: ${theme.colors.activeRedOrange};
`;
const FailedList = styled.ul`
	max-height: 360px;
	margin-top: 20px;
	overflow-y: auto;
	border: 1px solid ${theme.colors.ultraLightGrayBorder};
	border-radius: 6px;
`;
const FailedRow = styled.li`
	font-size: 13px;
	line-height: 18px;
	padding: 10px 16px;
	display: flex;
	gap: 12px;
	&:not(:last-child) {
		border-bottom: 1px solid ${theme.colors.ultraLightGrayBorder};
	}
`;
const RowIndex = styled.span`
	width: 48px;
	flex-shrink: 0;
	color: ${theme.colors.deepGrayFontColor};
`;
const RowEmail = styled.span`
	width: 180px;
	flex-shrink: 0;
	color: ${theme.colors.deepGrayFontColor};
`;
const RowReason = styled.span`
	color: ${theme.colors.blackColor};
`;
